import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Users, UserPlus, Share2, Copy, Check, Search, ChevronRight, Loader2, Wallet, ArrowDownLeft } from "lucide-react";
import { BottomNav } from "@/components/BottomNav";
import { useAuth } from "@/hooks/useAuth";
import { parrainageService } from "@/services/parrainageService";
import { depositService } from "@/services/depositService";
import { analyticsService } from "@/services/analytics";
import directus from "@/lib/directus";
import { readItems } from "@directus/sdk";
import { safeBack, copyToClipboard } from "@/lib/utils";
import { toast } from "sonner";

interface Parrainage {
  client_id: string;
  date_created?: string;
  date_updated?: string;
  numero_commercial?: string;
}

interface FilleulDepot {
  id?: string | number;
  account_id?: string;
  nom?: string;
  montant?: number;
  date_created?: string;
  numero_initial?: string;
  statut?: string;
}

type TabKey = "filleuls" | "depots";

function ParrainagePage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const numero_commercial = user?.phone_number;

  const [tab, setTab] = useState<TabKey>("filleuls");
  const [parrainages, setParrainages] = useState<Parrainage[]>([]);
  const [depots, setDepots] = useState<any[]>([]);
  const [filleulsDepots, setFilleulsDepots] = useState<FilleulDepot[]>([]);
  const [loadingDepots, setLoadingDepots] = useState(false);
  const [clientId, setClientId] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [query, setQuery] = useState("");
  const [copied, setCopied] = useState(false);
  const [backendEarnings, setBackendEarnings] = useState(0);

  useEffect(() => {
    analyticsService.trackScreenView("parrainage_page");
  }, []);

  useEffect(() => {
    const run = async () => {
      if (!numero_commercial) return;
      setLoading(true);
      const [parrainagesResult, depotsResult, relationsResult] = await Promise.all([
        parrainageService.fetchParrainageList(numero_commercial),
        depositService.fetchDepositHistory(numero_commercial),
        directus.request(
          readItems("commercial_relations", {
            filter: {
              commercial_phoneNumber: { _eq: numero_commercial },
            },
            fields: ["balance", "commission"],
            limit: 1,
          })
        ),
      ]);
      const list = (parrainagesResult?.parrainages as Parrainage[]) ?? [];
      setParrainages(list);
      setDepots((depotsResult?.depots as any[]) ?? []);
      const relation = Array.isArray(relationsResult) && relationsResult.length > 0 ? relationsResult[0] : null;
      setBackendEarnings(relation?.balance ?? 0);
      setLoading(false);

      // Dépôts des filleuls
      const clientIds = list.map((p) => p.client_id).filter(Boolean);
      if (clientIds.length > 0) {
        setLoadingDepots(true);
        const res = await parrainageService.fetchFilleulsDeposits(clientIds);
        setFilleulsDepots((res?.depots as FilleulDepot[]) ?? []);
        setLoadingDepots(false);
      } else {
        setFilleulsDepots([]);
      }
    };
    run();
  }, [numero_commercial]);

  const referralLink = numero_commercial
    ? `https://easyservicebf.web.app/share/${numero_commercial}`
    : "";

  const handleCopy = async () => {
    if (!referralLink) return;
    const ok = await copyToClipboard(referralLink);
    if (ok) {
      setCopied(true);
      toast.success("Lien copié !");
      setTimeout(() => setCopied(false), 2000);
    } else {
      toast.error("Impossible de copier le lien. Appuie longuement sur le lien pour le copier manuellement.");
    }
  };

  const handleShare = async () => {
    if (!referralLink) return;
    if (navigator.share) {
      try {
        await navigator.share({
          title: "Parrainage",
          text: "Rejoins-moi et gagne 250 FCFA à chaque parrainage validé !",
          url: referralLink,
        });
      } catch {
        /* annulation utilisateur */
      }
    } else {
      handleCopy();
    }
  };

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!numero_commercial || !clientId) return;
    setSubmitting(true);
    const result = await parrainageService.addParrainage({ numero_commercial, client_id: clientId });
    if (result.success) {
      const next = await parrainageService.fetchParrainageList(numero_commercial);
      const list = (next?.parrainages as Parrainage[]) ?? [];
      setParrainages(list);
      const clientIds = list.map((p) => p.client_id).filter(Boolean);
      if (clientIds.length > 0) {
        const res = await parrainageService.fetchFilleulsDeposits(clientIds);
        setFilleulsDepots((res?.depots as FilleulDepot[]) ?? []);
      }
      setClientId("");
      toast.success("Parrainage ajouté avec succès");
    } else {
      toast.error(result.error?.message ?? "Erreur lors de l'ajout du parrainage");
    }
    setSubmitting(false);
  };

  const depositedClientIds = new Set<string>([
    ...filleulsDepots.map((d) => String(d.account_id ?? "")),
    ...depots.map((d: any) => String(d.nom ?? d.account_id ?? "")),
  ]);

  const isValidated = (clientId: string) => depositedClientIds.has(String(clientId));

  const filtered = parrainages.filter((p) =>
    String(p.client_id || "").toLowerCase().includes(query.toLowerCase())
  );
  const filteredDepots = filleulsDepots.filter((d) =>
    String(d.account_id || "").toLowerCase().includes(query.toLowerCase())
  );
  const validCount = parrainages.filter((p) => isValidated(p.client_id)).length;
  const totalFilleulsDepots = filleulsDepots.reduce((sum, d) => sum + (Number(d.montant) || 0), 0);
  const earnings = backendEarnings;

  return (
    <div className="relative flex min-h-[100dvh] flex-col bg-background text-foreground [padding-top:env(safe-area-inset-top)]">
      <div
        aria-hidden
        className="pointer-events-none fixed inset-0 -z-10"
        style={{
          background:
            "radial-gradient(120% 80% at 50% 0%, var(--page-gradient-via) 0%, var(--page-gradient-from) 45%, var(--page-gradient-to) 100%)",
        }}
      />

      <header className="sticky top-0 z-30 border-b border-border bg-header-bg backdrop-blur-xl">
        <div className="mx-auto flex max-w-[52ch] items-center gap-3 px-4 py-3 lg:max-w-6xl lg:px-8">
          <button
            onClick={() => safeBack(navigate, "/parrainage")}
            aria-label="Retour"
            className="grid size-9 place-items-center rounded-full bg-muted ring-1 ring-card-muted-ring"
          >
            <ArrowLeft className="size-4" />
          </button>
          <div className="flex items-center gap-2">
            <Users className="size-4 text-accent-brand" />
            <h1 className="text-base font-semibold tracking-tight">Mes filleuls</h1>
          </div>
        </div>
      </header>

      <main className="mx-auto w-full max-w-[52ch] flex-1 space-y-4 px-4 py-4 lg:max-w-6xl lg:px-8">
        {/* Stats résumé */}
        <section className="grid grid-cols-2 gap-2">
          <div className="rounded-2xl bg-card-muted p-3.5 ring-1 ring-card-muted-ring">
            <p className="text-[10px] font-bold uppercase tracking-widest text-accent-brand">Filleuls</p>
            <p className="mt-1 text-xl font-bold tabular-nums">{parrainages.length}</p>
          </div>
          <div className="rounded-2xl bg-card-muted p-3.5 ring-1 ring-card-muted-ring">
            <p className="text-[10px] font-bold uppercase tracking-widest text-accent-brand">Validés</p>
            <p className="mt-1 text-xl font-bold tabular-nums text-brand-blue-light">
              {validCount}
              <span className="text-xs text-text-subtle"> / {parrainages.length}</span>
            </p>
          </div>
        </section>

        {/* Récompense par parrainage */}
        <section className="relative overflow-hidden rounded-2xl p-4 ring-1 ring-card-muted-ring bg-card-muted">
          <div className="flex items-center gap-3">
            <span className="grid size-11 shrink-0 place-items-center rounded-xl bg-accent-brand-soft">
              <span className="text-lg font-extrabold text-accent-brand">250</span>
            </span>
            <div className="min-w-0 flex-1">
              <p className="text-[10px] font-bold uppercase tracking-widest text-accent-brand">Récompense</p>
              <p className="mt-0.5 text-sm font-semibold">250 FCFA par parrainage</p>
              <p className="mt-0.5 text-[11px] leading-relaxed text-text-subtle">
                Gagne 250 FCFA pour chaque ami parrainé et validé.
              </p>
            </div>
          </div>
        </section>

        {/* Solde commissions */}
        <section className="relative overflow-hidden rounded-2xl p-4 ring-1 ring-card-muted-ring bg-card-muted">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <span className="grid size-11 shrink-0 place-items-center rounded-xl bg-accent-brand-soft">
                <Wallet className="size-5 text-accent-brand" />
              </span>
              <div className="min-w-0">
                <p className="text-[10px] font-bold uppercase tracking-widest text-accent-brand">Mes commissions</p>
                <p className="mt-0.5 text-2xl font-bold tabular-nums text-foreground">
                  {earnings.toLocaleString("fr-FR")} <span className="text-sm font-semibold">FCFA</span>
                </p>
              </div>
            </div>
            <button
              onClick={() => navigate("/affiliate-withdraw")}
              className="flex shrink-0 items-center gap-1.5 rounded-xl bg-accent-brand px-3.5 py-2 text-xs font-semibold text-white shadow-sm transition active:scale-95"
            >
              Retirer
            </button>
          </div>
        </section>

        {/* Lien de parrainage */}
        <section className="rounded-2xl bg-card-muted p-4 ring-1 ring-card-muted-ring">
          <div className="flex items-start gap-3">
            <span className="grid size-10 shrink-0 place-items-center rounded-xl bg-accent-brand-soft">
              <Share2 className="size-5 text-accent-brand" />
            </span>
            <div className="min-w-0 flex-1">
              <p className="text-[10px] font-bold uppercase tracking-widest text-accent-brand">Mon lien</p>
              <p className="mt-0.5 text-sm font-semibold">Invite tes amis</p>
              <p className="mt-1 text-[11px] leading-relaxed text-text-subtle">
                Partage ce lien. Gagne 250 FCFA à chaque parrainage validé.
              </p>
            </div>
          </div>

          <div className="mt-3 flex items-center gap-2 rounded-xl bg-background px-3 py-2.5 ring-1 ring-card-muted-ring">
            <span className="min-w-0 flex-1 truncate font-mono text-[11px] text-foreground">{referralLink || "—"}</span>
            <button
              onClick={handleCopy}
              className="grid size-8 shrink-0 place-items-center rounded-lg bg-accent-brand text-white"
              aria-label="Copier le lien"
            >
              {copied ? <Check className="size-3.5" /> : <Copy className="size-3.5" />}
            </button>
          </div>

          <button
            onClick={handleShare}
            className="mt-2.5 flex w-full items-center justify-center gap-2 rounded-xl bg-accent-brand py-2.5 text-sm font-semibold text-white shadow-sm transition active:scale-[0.98]"
          >
            <Share2 className="size-4" /> Partager mon lien
          </button>
        </section>

        {/* Retirer mes gains — visible immédiatement après le lien */}
        <button
          onClick={() => navigate("/affiliate-withdraw")}
          className="flex w-full items-center justify-between rounded-2xl bg-card-muted p-4 text-left ring-1 ring-card-muted-ring transition hover:bg-card-muted-3"
        >
          <span className="flex items-center gap-3">
            <span className="grid size-9 place-items-center rounded-xl bg-accent-brand-soft">
              <Wallet className="size-4 text-accent-brand" />
            </span>
            <span>
              <span className="block text-sm font-semibold">Retirer mes gains</span>
              <span className="block text-[11px] text-text-subtle">Demander un versement</span>
            </span>
          </span>
          <ChevronRight className="size-4 text-foreground/30" />
        </button>

        {/* Ajout manuel */}
        <section className="space-y-2 rounded-2xl bg-card-muted p-4 ring-1 ring-card-muted-ring">
          <p className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest text-accent-brand">
            <UserPlus className="size-3.5" /> Parrainer manuellement
          </p>
          <form onSubmit={handleAdd} className="flex items-center gap-2">
            <input
              type="text"
              inputMode="text"
              placeholder="Identifiant client à parrainer"
              value={clientId}
              onChange={(e) => setClientId(e.target.value)}
              required
              className="min-w-0 flex-1 rounded-xl bg-background px-3.5 py-2.5 text-base ring-1 ring-card-muted-ring outline-none focus:ring-2 focus:ring-accent-brand"
            />
            <button
              type="submit"
              disabled={submitting || !clientId}
              className="flex shrink-0 items-center gap-1.5 rounded-xl bg-accent-brand px-4 py-2.5 text-sm font-semibold text-white transition active:scale-95 disabled:opacity-50"
            >
              {submitting ? <Loader2 className="size-4 animate-spin" /> : <UserPlus className="size-4" />}
              <span className="hidden sm:inline">Parrainer</span>
            </button>
          </form>
        </section>

        {/* Onglets */}
        <div className="grid grid-cols-2 gap-1 rounded-2xl bg-card-muted p-1 ring-1 ring-card-muted-ring">
          <button
            onClick={() => setTab("filleuls")}
            className={
              "flex items-center justify-center gap-1.5 rounded-xl px-3 py-2.5 text-xs font-semibold transition " +
              (tab === "filleuls"
                ? "bg-accent-brand text-white shadow-sm"
                : "text-text-subtle hover:text-foreground")
            }
          >
            <Users className="size-3.5" /> Mes filleuls
            <span className="tabular-nums opacity-70">({parrainages.length})</span>
          </button>
          <button
            onClick={() => setTab("depots")}
            className={
              "flex items-center justify-center gap-1.5 rounded-xl px-3 py-2.5 text-xs font-semibold transition " +
              (tab === "depots"
                ? "bg-accent-brand text-white shadow-sm"
                : "text-text-subtle hover:text-foreground")
            }
          >
            <ArrowDownLeft className="size-3.5" /> Dépôts
            <span className="tabular-nums opacity-70">({filleulsDepots.length})</span>
          </button>
        </div>

        {/* Recherche */}
        <div className="flex items-center gap-2 rounded-xl bg-card-muted px-3 py-2.5 ring-1 ring-card-muted-ring">
          <Search className="size-4 text-text-subtle" />
          <input
            type="text"
            placeholder={tab === "filleuls" ? "Rechercher un filleul…" : "Rechercher par identifiant client…"}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="min-w-0 flex-1 bg-transparent text-base outline-none placeholder:text-text-subtle"
          />
        </div>

        {tab === "filleuls" ? (
          /* Liste des filleuls */
          <section className="space-y-2">
            <p className="px-1 text-[10px] font-bold uppercase tracking-widest text-accent-brand">
              Liste des filleuls
            </p>

            {loading ? (
              <div className="flex justify-center py-10">
                <Loader2 className="size-6 animate-spin text-accent-brand" />
              </div>
            ) : filtered.length === 0 ? (
              <div className="rounded-2xl bg-card-muted p-6 text-center text-sm text-text-subtle ring-1 ring-card-muted-ring">
                {parrainages.length === 0
                  ? "Aucun filleul pour l'instant. Partagez votre lien !"
                  : "Aucun résultat pour cette recherche."}
              </div>
            ) : (
              <ul className="space-y-2">
                {filtered.map((p, i) => {
                  const valid = isValidated(p.client_id);
                  return (
                    <li
                      key={`${p.client_id}-${i}`}
                      className="rounded-2xl bg-card-muted p-3.5 ring-1 ring-card-muted-ring"
                    >
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-3">
                          <span className="grid size-9 shrink-0 place-items-center rounded-xl bg-muted">
                            <Users className="size-4 text-text-subtle" />
                          </span>
                          <div className="min-w-0">
                            <p className="truncate text-sm font-semibold">{p.client_id}</p>
                            <p className="mt-0.5 text-[11px] text-text-subtle">
                              {p.date_created?.slice(0, 10) ?? "—"}
                            </p>
                          </div>
                        </div>
                        <span
                          className={
                            "rounded-full px-2 py-0.5 text-[10px] font-bold " +
                            (valid
                              ? "bg-brand-blue-soft text-brand-blue"
                              : "bg-muted text-muted-foreground ring-1 ring-card-muted-ring")
                          }
                        >
                          {valid ? "Validé" : "En attente"}
                        </span>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </section>
        ) : (
          /* Liste des dépôts des filleuls */
          <section className="space-y-2">
            <div className="flex items-center justify-between gap-2 px-1">
              <p className="text-[10px] font-bold uppercase tracking-widest text-accent-brand">
                Dépôts de mes filleuls
              </p>
              <p className="text-[11px] font-semibold tabular-nums text-text-subtle">
                Total {totalFilleulsDepots.toLocaleString("fr-FR")} FCFA
              </p>
            </div>

            {loading || loadingDepots ? (
              <div className="flex justify-center py-10">
                <Loader2 className="size-6 animate-spin text-accent-brand" />
              </div>
            ) : filteredDepots.length === 0 ? (
              <div className="rounded-2xl bg-card-muted p-6 text-center text-sm text-text-subtle ring-1 ring-card-muted-ring">
                {filleulsDepots.length === 0
                  ? "Aucun dépôt effectué par vos filleuls pour l'instant."
                  : "Aucun résultat pour cette recherche."}
              </div>
            ) : (
              <ul className="space-y-2">
                {filteredDepots.map((d, i) => (
                  <li
                    key={`${d.id ?? d.account_id}-${i}`}
                    className="rounded-2xl bg-card-muted p-3.5 ring-1 ring-card-muted-ring"
                  >
                    <div className="flex items-start gap-3">
                      <span className="grid size-9 shrink-0 place-items-center rounded-xl bg-accent-brand-soft">
                        <ArrowDownLeft className="size-4 text-accent-brand" />
                      </span>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center justify-between gap-2">
                          <p className="truncate text-sm font-semibold">{d.account_id || d.nom || "—"}</p>
                          <p className="whitespace-nowrap text-sm font-bold tabular-nums">
                            {(Number(d.montant) || 0).toLocaleString("fr-FR")}{" "}
                            <span className="text-xs text-text-subtle">FCFA</span>
                          </p>
                        </div>
                        <div className="mt-1 flex items-center justify-between gap-2 text-[11px] text-text-subtle">
                          <span className="truncate">Dépôt filleul</span>
                          <span className="whitespace-nowrap">{d.date_created?.slice(0, 10) ?? "—"}</span>
                        </div>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </section>
        )}
      </main>

      <BottomNav />
    </div>
  );
}

export default ParrainagePage;